import PropTypes from "prop-types";
import React, { useContext } from "react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { Formik, Form } from "formik";
import { InputField } from "../../forms/inputGroup";
import {
  StyleTypeContext,
  TranslationContext,
  countryCodes,
} from "../../../umbilical_tools";
import { getGuestType, hiddenFields } from "../ActivityBookingProcess/helpers";
import { DateTime } from "luxon";
import { DatepickerField, SelectField } from "../../forms/inputGroup/helpers";
import jssVariables from "../../../jss_setup";
import { Str } from "@supercharge/strings";

function getField({ form, fieldName, reactKey, customStyles }) {
  const { t } = useContext(TranslationContext);

  if (typeof form !== "object") {
    return null;
  }

  var label = t(_.camelCase(form.label));
  // If it's camel case that indicates there is no translation for it
  if (Str(label).isCamel()) {
    label = form.label;
  }

  const commonProps = {
    key: reactKey,
    as: "input",
    label,
    name: fieldName,
    customStyles: { ...fieldCustomStyles, ...customStyles },
    placeholder: "----",
    isRequired: form.required,
  };

  switch (form.input_type) {
    case "charfield":
      if (_.lowerCase(form.label) === "nationality") {
        return (
          <SelectField
            options={Object.entries(countryCodes).map(([key, value]) => [
              value,
              `${key} (${value})`,
            ])}
            {...commonProps}
          />
        );
      }
      return <InputField {...commonProps} />;
    case "emailfield":
      return <InputField {...commonProps} />;
    case "choicefield":
      return <SelectField options={form.choices} {...commonProps} />;
    case "datefield":
      return (
        <DatepickerField
          dateFormat="MMM d, yyyy"
          withPortal={true}
          minDate={DateTime.now().minus({ year: 99 }).toISO()}
          maxDate={DateTime.now().toISO()}
          customStyles={{}}
          showYearDropdown={true}
          yearDropdownItemsNum={90}
          {...commonProps}
        />
      );
    default:
      return null;
  }
}

const fieldCustomStyles = {
  label: { textTransform: "capitalize" },
};
const styles = createUseStyles({
  travellerForm: (props) => ({
    borderRadius: jssVariables.border_radius,
    ..._.get(props, "customStyles.travellerForm", {}),
  }),
  guestHeader: (props) => ({
    display: "grid",
    textTransform: "capitalize",
    fontSize: "large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.guestHeader", {}),
  }),
  productTitle: (props) => ({
    fontSize: "medium",
    fontWeight: "normal",
    ..._.get(props, "customStyles.productTitle", {}),
  }),
  form: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gridGap: `calc(${jssVariables.normal_gap} * 1)`,
    justifyContent: "space-between",
    ..._.get(props, "customStyles.form", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    travellerForm: (props) => ({
      border: `2px solid ${jssVariables.colors.base_color}`,
      padding: `calc(${jssVariables.normal_gap} * 1)`,
      width: "25rem",
      justifySelf: "center",
      ..._.get(props, "customStyles.travellerForm.smallscreen", {}),
    }),
    form: (props) => ({
      ..._.get(props, "customStyles.form.smallscreen", {}),
    }),
  },
});
const TravellerForm = ({ idx, formStructure, guest, setGuests }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "ActivityTravellerForm",
  });
  const { t } = useContext(TranslationContext);

  const classes = styles({ customStyles });
  function fielder(key, idx) {
    return (
      formStructure?.[key] &&
      !hiddenFields.includes(key) &&
      getField({
        form: formStructure[key],
        fieldName: key,
        reactKey: `${idx}_${key}`,
        customStyles,
      })
    );
  }

  const standardInputs = ["title", "first_name", "last_name", "email"];

  return (
    <div className={classes.travellerForm}>
      <span className={classes.guestHeader}>
        {guest.is_customer ? t("leadGuest") : t("guest")}{" "}
        {!guest.is_customer && idx}
        {" - "}
        {getGuestType({ guest_type: guest.guest_type, t })}
      </span>
      {formStructure.product_name && (
        <span className={classes.productTitle}>
          {`${t("ticketType")}: ${formStructure.product_name}`}
        </span>
      )}
      <Formik
        initialValues={{ ...guest.form }}
        validateOnMount={false}
        validateOnBlur={false}
        validate={(values) => {
          setGuests(
            guest.guest_uuid,
            values.date_of_birth
              ? { ...values, date_of_birth: values.date_of_birth[0] }
              : values
          );
        }}>
        <Form className={classes.form}>
          {guest.guest_type === "ADT" && fielder("title", 0)}
          {fielder("first_name", 1)}
          {fielder("last_name", 2)}
          {Object.entries(formStructure)
            .filter(
              (entry) => entry[0] !== "is_customer" || entry !== "guest_uuid"
            )
            .filter((entry) => !standardInputs.includes(entry[0]))
            .filter((entry) => _.get(entry[1], "required", false))
            .map((entry, idx) => fielder(entry[0], 3 + idx))}
          {guest.guest_type === "ADT" && fielder("email", 1000)}
        </Form>
      </Formik>
    </div>
  );
};
TravellerForm.defaultProps = {
  idx: 0,
  formStructure: {},
  guest: {},
};
TravellerForm.propTypes = {
  idx: PropTypes.number.isRequired,
  formStructure: PropTypes.object.isRequired,
  guest: PropTypes.object.isRequired,
  setGuests: PropTypes.func.isRequired,
};

export default TravellerForm;
